import { yupResolver } from "@hookform/resolvers/yup";
import React, { useEffect, useState } from "react";
import { Button, Form, Spinner } from "react-bootstrap";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import * as Yup from "yup";

import { useAppSelector } from "../../../../app/hook";
import { AlertsType } from "../../../../constants/alerts.constant";
import { selectUseId } from "../../../../features/auth/auth.slice";
import useComponentVisible from "../../../../hooks/useComponentVisible";
import { BlockType } from "../../../../interfaces/models/Knowledge/scenario.interface";
import AiQC from "../../../common/AiQC";
import CopyVariable from "../../../common/CopyVariable";
import HintBlock from "../../../common/HintBlock";
import { createRandomString, isAlphabetic } from "../../../tools";
import AddNoteButton from "../AddNoteButton";
import DeleteButton from "../DeleteButton";
import NodeParentChanger from "../NodeParentChanger";
import NoteForm, { getNoteSchema, INoteForm } from "../NoteForm";
import ScenarioHeader from "../ScenarioHeader";
import { MacBotActionKind } from "../types";
import { MacBotButtonSettingForm } from "./scenario-button-setting-form.component";
import ScenarioSide from "./scenario-side";
import { ScenarioSimpleTextInput } from "./scenario-simple-text-input";

export const MacBotReceiveAttachmentForm = ({
  setShow,
  itemData,
  parentId,
  tree,
  config,
  loading,
  createScenarioBlock,
  deleteScenarioBlock,
  changeScenarioBlock
}: {
  setShow?: (item: boolean) => void;
  itemData: any;
  parentId: string | null;
  tree: any[];
  config: any;
  loading: boolean;
  createScenarioBlock: (block: any) => Promise<any>;
  deleteScenarioBlock: (block: any) => void;
  changeScenarioBlock: (block: any) => Promise<any>;
}) => {
  const { t } = useTranslation();
  const [showButtonSetting, setShowButtonSetting] = useState(false);
  const [selectedButtonToEdit, setSelectedButtonToEdit] = useState<number>(-1);
  const [quickReplies, setQuickReplies] = useState<any[]>([]);
  const [showDeleteButton, setShowDeleteButton] = useState(true);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const userId = useAppSelector(selectUseId);
  const { ref, isComponentVisible } = useComponentVisible(showContextMenu);
  const [cloneJson, setCloneJson] = useState<IResponseForm | null>(null);

  interface IResponseForm extends INoteForm {
    BlockTitle?: string;
    BotResponse: string;
    isAiQcActive: boolean;
  }

  const schema = Yup.object({
    BlockTitle: Yup.string()
      .required(t(AlertsType.ISKBKO025))
      .max(30, t("Wizard/validationLabels/key16")),
    BotResponse: Yup.string().max(150).required(t(AlertsType.ISKBKO026)),
    isAiQcActive: Yup.boolean(),
    ...getNoteSchema(t)
  });

  const {
    register,
    formState: { errors },
    reset,
    watch,
    trigger,
    getValues,
    setValue,
    handleSubmit,
    control
  } = useForm<IResponseForm>({
    resolver: yupResolver(schema) as any,
    mode: "onSubmit",
    defaultValues: {
      notes: [
        {
          note_id: uuidv4(),
          creation_date_time: new Date().toISOString(),
          title: "",
          description: "",
          qc: false,
          author: userId
        }
      ]
    }
  });
  const { fields } = useFieldArray({
    control,
    name: "notes"
  });
  //Check Show Button Delete
  useEffect(() => {
    if (itemData) {
      const find = tree.find((dr) => dr.id == itemData.id);
      if (find && find.parent == null) {
        setShowDeleteButton(false);
      }
    }
  }, [tree]);

  const [contextMenuList, setContextMenuList] = useState<any[]>([]);

  useEffect(() => {
    reset({
      BlockTitle: itemData?.title ?? "",
      BotResponse: itemData?.message ?? "",
      isAiQcActive: itemData?.isAiQcActive ?? false,
      notes: itemData?.notes
    });

    setCloneJson({
      BlockTitle: itemData?.title ?? "",
      BotResponse: itemData?.message ?? "",
      showNoteForm: itemData?.notes?.length > 0 || false,
      isAiQcActive: itemData?.isAiQcActive ?? false,
      notes: itemData?.notes
    });

    if (!itemData) {
      setShowDeleteButton(false);
    }
  }, [itemData]);

  const deleteHandler = () => {
    deleteScenarioBlock(itemData);
  };

  const removeButtonHandler = (btnIndex: number) => {
    setQuickReplies((prev) => prev.filter((p, index) => index !== btnIndex));
  };
  const addButtonHandler = (qBtn: number) => {
    setQuickReplies((prev) => [...prev, qBtn]);
  };

  const editButtonHandler = (_index: number, qRep: any) => {
    setQuickReplies((prev) =>
      prev.map((p, index) => (index === _index ? qRep : p))
    );

    setSelectedButtonToEdit(-1);
  };

  const closeButtonSettingModal = () => {
    setShowButtonSetting(false);
    setSelectedButtonToEdit(-1);
  };

  const createTitle = (text: string) => {
    let titleCode = 0;
    tree.forEach((element) => {
      if (element.type === BlockType.Response) {
        const _code = element.title.split("-");
        if (_code[0] && Number(_code[0]) > titleCode) titleCode = +_code[0];
      }
    });

    if (typeof text != "string" || !text) {
      return "";
    }
    let acronym = text
      ?.match(/[\p{Alpha}\p{Nd}]+/gu)
      ?.reduce(
        (previous, next) =>
          previous +
          (+next === 0 || parseInt(next) ? parseInt(next) : next[0] || ""),
        ""
      )
      ?.toUpperCase();

    if (!acronym || (acronym && acronym.length < 3) || !isAlphabetic(acronym)) {
      acronym = createRandomString(5);
    }

    return `${titleCode + 1}-${acronym?.slice(0, 3)}`;
  };

  const ButtonSubmit = (data: IResponseForm) => {
    if (itemData) {
      const _data = {
        ...itemData,
        id: itemData.id,
        type: BlockType.ReceiveAttachment,
        title: data.BlockTitle,
        message: data.BotResponse,
        parent: itemData.parent,
        isAiQcActive: data.isAiQcActive,
        notes: data.notes
      };

      if (
        JSON.stringify(cloneJson) ==
        JSON.stringify({
          BlockTitle: data.BlockTitle,
          BotResponse: data.BotResponse,
          isAiQcActive: data.isAiQcActive,
          notes: data.notes
        })
      ) {
        setShow?.(false);
        return;
      }

      changeScenarioBlock(_data).then(() => {
        reset();
        setShow?.(false);
      });
    } else {
      const _data = {
        type: BlockType.ReceiveAttachment,
        title: data.BlockTitle,
        message: data.BotResponse,
        parent: parentId,
        isAiQcActive: data.isAiQcActive,
        notes: data.notes
      };
      createScenarioBlock(_data).then(() => {
        reset();
        setShow?.(false);
      });
    }
  };

  const getVariableName = () => {
    const data = watch("BlockTitle");
    if (data) {
      return `${data}_user_file`;
    }

    return "";
  };

  useEffect(() => {
    setShowContextMenu(false);
  }, [isComponentVisible]);

  useEffect(() => {
    if (config?.metaData?.variables) {
      setContextMenuList([...config?.metaData?.variables]);
    }
  }, [config]);

  return (
    <React.Fragment>
      <ScenarioSide size="md" onSubmit={handleSubmit(ButtonSubmit)}>
        <ScenarioHeader
          type={MacBotActionKind.ShowReceiveAttachment}
          onClose={() => {
            reset();
            setShow?.(false);
          }}
        />

        <div className="tw-w-full tw-h-full tw-px-3 tw-py-2 tw-flex-col tw-justify-start tw-items-start tw-gap-4 tw-inline-flex tw-font-[Lexend]">
          <Form.Group className="tw-w-full">
            <Form.Label className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
              {t("MacBotResponseForm/key1")}
            </Form.Label>
            <Form.Control
              max={30}
              maxLength={30}
              {...register("BlockTitle")}
              className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
            />
            <HintBlock texts={["MacBotResponseForm/maxlenght"]} />
            <Form.Control.Feedback
              type="invalid"
              className="d-block font-poppins"
            >
              {errors.BlockTitle?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="tw-w-full ">
            <Form.Label className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
              {t("MacBotResponseForm/key2")}
            </Form.Label>

            <Form.Control
              className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
              isInvalid={!!errors.BotResponse}
              maxLength={150}
              {...register(`BotResponse`)}
            />
            <HintBlock texts={["MacBotAttachmentForm/maxlenght"]} />

            <Form.Control.Feedback
              type="invalid"
              className="d-block font-poppins"
            >
              {errors.BotResponse?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="tw-w-full ">
            <Form.Label
              htmlFor=""
              className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none"
            >
              {t("MacBotCreateVariableForm/key5")}
            </Form.Label>

            <CopyVariable text={getVariableName()} noTitle={true} />
          </Form.Group>

          <div className="tw-mt-3 tw-pb-6 tw-w-full">
            <AiQC setValue={setValue} name="isAiQcActive" watch={watch} />
          </div>
        </div>
        <div className="tw-flex flex-row tw-justify-between tw-items-center">
          <div className="tw-flex tw-justify-start tw-items-center tw-gap-2">
            {showDeleteButton && <DeleteButton deleteHandler={deleteHandler} />}

            {itemData?.id && (
              <NodeParentChanger tree={tree} currentId={itemData.id} />
            )}
            <AddNoteButton noteFormMethods={{ getValues, setValue }} />
          </div>
          <div className="tw-flex tw-justify-end">
            <Button
              type="submit"
              className="tw-h-11 tw-px-5 tw-py-3 tw-bg-primary tw-rounded-[99px] tw-justify-center tw-items-center tw-inline-flex"
              disabled={loading}
            >
              {loading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                t("MacBotResponseForm/btn-02")
              )}
            </Button>
          </div>
        </div>
        <NoteForm
          noteFormMethods={{ register, watch, errors, fields, setValue }}
        />
      </ScenarioSide>

      <MacBotButtonSettingForm
        show={showButtonSetting}
        closeModal={closeButtonSettingModal}
        addQuickReplies={addButtonHandler}
        editQuickReplies={editButtonHandler}
        selectedButtonToEdit={selectedButtonToEdit}
        quickReplies={quickReplies}
        removeButton={removeButtonHandler}
        tree={tree}
      />
    </React.Fragment>
  );
};
